import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import { useNavigate } from 'react-router-dom'
import CircularProgress from '@mui/material/CircularProgress'
import { FaCheckCircle, FaRegEdit, FaSearch, FaTimesCircle } from "react-icons/fa"
import { FiPlus } from 'react-icons/fi'
import { GoUnlink } from "react-icons/go"
import { HiMiniPower } from "react-icons/hi2"
import { IoCopyOutline } from 'react-icons/io5'
import { MdRadioButtonUnchecked } from 'react-icons/md'
import { PopupWindow } from '../../PopupWindow'
import PopupDropdown from '../../PopupDropdown'
import { formatDate } from '../../helpers/formatDate'
import { toast } from "react-toastify"
import httpClient from '../../helpers/httpClient'
import {
  formatPropertyIdentifier,
  isValidPartition,
  normalizePartition,
  parsePropertyIdentifier,
} from '../../helpers/propertyIdentifier'
import Button from '../../UI/Button'

const createEmptyPropertyValidation = () => ({
  status: 'unvalidated',
  message: '',
  parcel: null,
  normalized_capakey: '',
  validation_source_status: null,
  suggested_property_updates: {
    cadastral_nr: '',
    location: null,
  },
})

const createEmptyCapakeyLookupCriteria = () => ({
  street_name: '',
  house_number: '',
  box_number: '',
  postal_code: '',
  municipality_name: '',
})

const buildPropertyValidationFromProperty = (property) => {
  const validation = createEmptyPropertyValidation()
  if (!property) {
    return validation
  }

  return {
    ...validation,
    status: property.cadastral_validation_status || 'unvalidated',
    normalized_capakey: property.cadastral_nr || '',
    suggested_property_updates: {
      cadastral_nr: property.cadastral_nr || '',
      location: property.location || null,
    },
  }
}

const getPropertyValidationLabel = (status) => {
  if (status === 'validated') return 'Validated'
  if (status === 'not_found') return 'No official match'
  if (status === 'invalid_format') return 'Invalid format'
  if (status === 'service_error') return 'Validation unavailable'
  return 'Not validated'
}

const getPropertyValidationActionLabel = (status) => {
  if (status === 'validated') return 'Validated'
  if (status === 'not_found') return 'No match'
  if (status === 'invalid_format' || status === 'service_error') return 'Retry'
  return 'Validate'
}

const getEffectivePropertyValidationStatus = (status, validation) => {
  if (!validation?.status || validation.status === 'unvalidated') {
    return status || 'unvalidated'
  }

  return validation.status
}

const getPartitionHelper = (partitionNr) => {
  if (!partitionNr) return { message: 'Optional. Enter P followed by 4 digits.', isInvalid: false }
  if (!isValidPartition(partitionNr)) return { message: 'Use format P0017.', isInvalid: true }
  return { message: 'Entered manually; format checked only.', isInvalid: false }
}

const ValidationStatusIcon = ({ status, isLoading = false }) => {
  if (isLoading) {
    return (
      <span className='property-validation-icon is-loading' title='Validating'>
        <CircularProgress size={16} thickness={5} color='inherit' />
      </span>
    )
  }

  if (status === 'validated') {
    return <FaCheckCircle className='property-validation-icon is-valid' title='Validated' />
  }

  if (status === 'not_found' || status === 'invalid_format' || status === 'service_error') {
    return <FaTimesCircle className='property-validation-icon is-invalid' title={getPropertyValidationLabel(status)} />
  }

  return <MdRadioButtonUnchecked className='property-validation-icon is-unvalidated' title='Not validated' />
}

const AddressCopyButton = ({ address, onCopy }) => {
  if (!address) return null

  return (
    <Button
      variant='ghost'
      iconOnly
      className='property-validation-copy-btn'
      onClick={() => onCopy(address)}
      title='Use this address as location'
      aria-label='Use this address as location'
    >
      <IoCopyOutline />
    </Button>
  )
}

const PropertyValidationPanel = ({ validation, onUseSuggestedLocation, disabled }) => {
  if (!validation) return null

  const hasParcelInfo = Boolean(validation.parcel)
  const hasMessage = Boolean(validation.message)
  if (!hasParcelInfo && !hasMessage) return null

  return (
    <div className='property-validation-panel'>
      <div className='property-validation-panel__header'>
        <div className='property-validation-panel__title-wrap'>
          <div className='property-validation-panel__title'>Official parcel match</div>
          <div className={`property-validation-pill status-${validation.status || 'unvalidated'}`}>
            <ValidationStatusIcon status={validation.status} />
            <span>{getPropertyValidationLabel(validation.status)}</span>
          </div>
        </div>
      </div>
      {hasMessage && validation.status !== 'validated' && <div className='property-validation-panel__message'>{validation.message}</div>}
      {hasParcelInfo && (
        <div className='property-validation-grid'>
          <div className='property-validation-row highlight'>
            <span className='property-validation-row__label'>Capakey</span>
            <span className='property-validation-row__value'>{validation.parcel.capakey}</span>
          </div>
          <div className='property-validation-row'>
            <span className='property-validation-row__label'>Municipality</span>
            <span className='property-validation-row__value'>{validation.parcel.municipalityName} ({validation.parcel.municipalityCode})</span>
          </div>
          <div className='property-validation-row'>
            <span className='property-validation-row__label'>Department</span>
            <span className='property-validation-row__value'>{validation.parcel.departmentName} ({validation.parcel.departmentCode})</span>
          </div>
          <div className='property-validation-row'>
            <span className='property-validation-row__label'>Section</span>
            <span className='property-validation-row__value'>{validation.parcel.sectionCode}</span>
          </div>
          <div className='property-validation-row'>
            <span className='property-validation-row__label'>Perceelnummer</span>
            <span className='property-validation-row__value'>{validation.parcel.perceelnummer}</span>
          </div>
          <div className='property-validation-row'>
            <span className='property-validation-row__label'>Grondnummer</span>
            <span className='property-validation-row__value'>{validation.parcel.grondnummer}</span>
          </div>
          <div className='property-validation-row'>
            <span className='property-validation-row__label'>Exponent</span>
            <span className='property-validation-row__value'>{validation.parcel.exponent}</span>
          </div>
          <div className='property-validation-row'>
            <span className='property-validation-row__label'>Bisnummer</span>
            <span className='property-validation-row__value'>{validation.parcel.bisnummer}</span>
          </div>
          <div className='property-validation-row'>
            <span className='property-validation-row__label'>Macht</span>
            <span className='property-validation-row__value'>{validation.parcel.macht}</span>
          </div>
          {validation.parcel.adres?.length > 0 && validation.parcel.adres.map((address, index) => (
            <div className='property-validation-row address' key={`${address}-${index}`}>
              <span className='property-validation-row__label'>{index === 0 ? 'Address' : 'Address alt.'}</span>
              <span className='property-validation-row__value'>{address}</span>
              {!disabled && <AddressCopyButton address={address} onCopy={onUseSuggestedLocation} />}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const CapakeyLookupPanel = ({
  idPrefix,
  criteria,
  municipalities,
  municipalitiesLoading,
  onCriteriaChange,
  onSearch,
  results,
  message,
  isLoading,
  onSelectCandidate,
  disabled,
}) => {
  return (
    <div className='capakey-lookup-panel'>
      <div className='capakey-lookup-panel__header'>
        <div>
          <div className='capakey-lookup-panel__title'>Find capakey by official address</div>
        </div>
        <Button
          variant='secondary'
          size='sm'
          shape='pill'
          onClick={onSearch}
          disabled={disabled || isLoading}
          leftIcon={isLoading ? <CircularProgress size={14} thickness={5} color='inherit' /> : <FaSearch />}
        >
          {isLoading ? 'Searching...' : 'Search'}
        </Button>
      </div>

      <div className='capakey-lookup-panel__form'>
        <div className='input_field--combo-grid'>
          <label className='input_field__label' htmlFor={`${idPrefix}_capakey_lookup_street`}>Street *</label>
          <input
            className='input_field__input_box--text'
            id={`${idPrefix}_capakey_lookup_street`}
            type='text'
            value={criteria.street_name}
            onChange={(e) => onCriteriaChange('street_name', e.currentTarget.value)}
            disabled={disabled || isLoading}
          />
        </div>
        <div className='capakey-lookup-panel__row capakey-lookup-panel__row--compact'>
          <div className='input_field--combo-grid'>
            <label className='input_field__label' htmlFor={`${idPrefix}_capakey_lookup_house_number`}>No. *</label>
            <input
              className='input_field__input_box--text'
              id={`${idPrefix}_capakey_lookup_house_number`}
              type='text'
              value={criteria.house_number}
              onChange={(e) => onCriteriaChange('house_number', e.currentTarget.value)}
              disabled={disabled || isLoading}
            />
          </div>
          <div className='input_field--combo-grid'>
            <label className='input_field__label' htmlFor={`${idPrefix}_capakey_lookup_box_number`}>Box</label>
            <input
              className='input_field__input_box--text'
              id={`${idPrefix}_capakey_lookup_box_number`}
              type='text'
              value={criteria.box_number}
              onChange={(e) => onCriteriaChange('box_number', e.currentTarget.value)}
              disabled={disabled || isLoading}
            />
          </div>
          <div className='input_field--combo-grid'>
            <label className='input_field__label' htmlFor={`${idPrefix}_capakey_lookup_postal_code`}>Postal code</label>
            <input
              className='input_field__input_box--text'
              id={`${idPrefix}_capakey_lookup_postal_code`}
              type='text'
              value={criteria.postal_code}
              onChange={(e) => onCriteriaChange('postal_code', e.currentTarget.value)}
              disabled={disabled || isLoading}
            />
          </div>
        </div>
        <div className='input_field--combo-grid capakey-lookup-panel__municipality'>
          <label className='input_field__label'>Municipality or postal code *</label>
          <PopupDropdown
            options={municipalities.map((municipality) => municipality.municipality_name)}
            value={criteria.municipality_name}
            onChange={(value) => onCriteriaChange('municipality_name', value)}
            title={municipalitiesLoading ? 'Loading municipalities...' : 'Select municipality'}
            searchBar
            disabled={disabled || isLoading || municipalitiesLoading}
            clearable
            hasBorder={false}
            formatType='input-field'
            width='150px'
            padding='10px 12px'
            inputBoxHeight='42px'
            fontSize='0.8rem'
          />
        </div>
      </div>

      {message && <div className='capakey-lookup-panel__message'>{message}</div>}

      {results.length > 0 && (
        <div className='capakey-lookup-results'>
          {results.map((result) => (
            <div className='capakey-lookup-result' key={result.address_object_id || `${result.address}-${result.score}`}>
              <div className='capakey-lookup-result__header'>
                <div className='capakey-lookup-result__address'>{result.address}</div>
                {typeof result.score === 'number' && (
                  <div className='capakey-lookup-result__score'>Score {Math.round(result.score)}</div>
                )}
              </div>
              <div className='capakey-lookup-result__meta'>
                {result.officially_assigned ? 'Official address' : 'Unofficial match'}
                {result.address_status ? ` • ${result.address_status}` : ''}
              </div>

              {result.capakey_candidates?.length > 0 ? (
                <div className='capakey-lookup-result__candidates'>
                  {result.capakey_candidates.map((candidate) => (
                    <Button
                      key={candidate.parcel_object_id || candidate.capakey}
                      variant='secondary'
                      size='sm'
                      onClick={() => onSelectCandidate(candidate, result)}
                      disabled={disabled || isLoading}
                    >
                      {candidate.capakey}
                    </Button>
                  ))}
                </div>
              ) : (
                <div className='capakey-lookup-result__empty'>No parcel is linked to this official address.</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const PropertySection = forwardRef(({ unit, loading, creatingProperty, setCreatingProperty }, ref) => {
  const navigate = useNavigate()
  const [selectedProperty, setSelectedProperty] = useState(null)
  const [selectedPropertySection, setSelectedPropertySection] = useState("details")
  const [editProperty, setEditProperty] = useState(false)
  const [linkPropertyIsActive, setLinkPropertyIsActive] = useState(false)
  const [propertySearchValue, setPropertySearchValue] = useState("")
  const [properties, setProperties] = useState([])
  const [propertiesOfUnit, setPropertiesOfUnit] = useState([])
  const initialPropertyState = {
    cadastral_nr: '',
    partition_nr: '',
    location: '',
    function: '',
    surface: '',
    GFAa_underground: '',
    GFAa: '',
    GFAb: '',
    GFAc: '',
    EPC_label: '',
    description: '',
    co_ownership_share_numerator: '',
    co_ownership_share_denominator: '',
    cadastral_validation_status: 'unvalidated',
    cadastral_validated_at: null,
  }
  const [newPropertyObj, setNewPropertyObj] = useState(initialPropertyState)
  const [newPropertyValidation, setNewPropertyValidation] = useState(createEmptyPropertyValidation())
  const [selectedPropertyValidation, setSelectedPropertyValidation] = useState(createEmptyPropertyValidation())
  const [validatingNewProperty, setValidatingNewProperty] = useState(false)
  const [validatingSelectedProperty, setValidatingSelectedProperty] = useState(false)
  const [newPropertyLookupIsOpen, setNewPropertyLookupIsOpen] = useState(false)
  const [selectedPropertyLookupIsOpen, setSelectedPropertyLookupIsOpen] = useState(false)
  const [newPropertyLookupCriteria, setNewPropertyLookupCriteria] = useState(createEmptyCapakeyLookupCriteria())
  const [selectedPropertyLookupCriteria, setSelectedPropertyLookupCriteria] = useState(createEmptyCapakeyLookupCriteria())
  const [newPropertyLookupResults, setNewPropertyLookupResults] = useState([])
  const [selectedPropertyLookupResults, setSelectedPropertyLookupResults] = useState([])
  const [newPropertyLookupMessage, setNewPropertyLookupMessage] = useState('')
  const [selectedPropertyLookupMessage, setSelectedPropertyLookupMessage] = useState('')
  const [searchingNewPropertyCapakey, setSearchingNewPropertyCapakey] = useState(false)
  const [searchingSelectedPropertyCapakey, setSearchingSelectedPropertyCapakey] = useState(false)
  const [municipalities, setMunicipalities] = useState([])
  const [loadingMunicipalities, setLoadingMunicipalities] = useState(false)
  const [municipalitiesRequested, setMunicipalitiesRequested] = useState(false)

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    handleCreateProperty
  }))

  // Add useEffect for initial data loading
  useEffect(() => {
    if (unit?.id) {
      fetchPropertyFns()
    }
  }, [unit])

  useEffect(() => {
    setSelectedPropertyValidation(buildPropertyValidationFromProperty(selectedProperty))
    setSelectedPropertyLookupIsOpen(false)
    setSelectedPropertyLookupCriteria(createEmptyCapakeyLookupCriteria())
    setSelectedPropertyLookupResults([])
    setSelectedPropertyLookupMessage('')
  }, [selectedProperty])

  useEffect(() => {
    if (!creatingProperty) {
      setNewPropertyLookupIsOpen(false)
    }
  }, [creatingProperty])

  useEffect(() => {
    if (!editProperty) {
      setSelectedPropertyLookupIsOpen(false)
    }
  }, [editProperty])

  useEffect(() => {
    if ((newPropertyLookupIsOpen || selectedPropertyLookupIsOpen) && municipalities.length === 0 && !loadingMunicipalities && !municipalitiesRequested) {
      fetchMunicipalities()
    }
  }, [newPropertyLookupIsOpen, selectedPropertyLookupIsOpen, municipalities.length, loadingMunicipalities, municipalitiesRequested])

  // Add property update function
  const handleUpdateProperty = async () => {
    if (!selectedProperty) return
    if (!isValidPartition(selectedProperty.partition_nr)) {
      toast.error('Partition must use format P0017')
      return
    }

    const data = { ...selectedProperty }
    try {
      const response = await httpClient.post(import.meta.env.VITE_API_URL + `/api/properties/update`, data)
      if (response.status !== 200) {
        toast.error(response.data?.error || 'Failed to update property')
        return
      }

      toast.success('Property has been updated!')
      setEditProperty(false)
      fetchPropertyFns()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update property')
    }
  }

  // Add save button to property details when editing
  const renderPropertyDetailsActions = () => {
    if (!editProperty) return null

    return (
      <div className="property-details-actions">
        <Button variant='secondary' size='sm' onClick={handleUpdateProperty}>
          Save Changes
        </Button>
        <Button variant='secondary' tone='red' size='sm' onClick={() => setEditProperty(false)}>
          Cancel
        </Button>
      </div>
    )
  }

  const handleLinkProperty = async (propertyID) => {
    const data = {
      propertyID: propertyID,
      unitID: unit.id
    }
    await toast.promise(httpClient.post(import.meta.env.VITE_API_URL + `/api/properties/link`, data), {
      success: `Property has been linked!`,
      error: "Something went wrong",
      pending: `Linking property...`
    })

    fetchPropertyFns()
  }

  const handleUnlinkProperty = async () => {
    const data = {
      propertyID: selectedProperty.id,
      unitID: unit.id
    }
    await toast.promise(httpClient.post(import.meta.env.VITE_API_URL + `/api/properties/link?type=unlink`, data), {
      success: `Property has been unlinked!`,
      error: "Something went wrong",
      pending: `Unlinking property...`
    })

    setSelectedProperty(null)
    fetchPropertyFns()
  }

  const handleTogglePropertyStatus = async () => {
    const data = {
      propertyID: selectedProperty.id
    }
    await toast.promise(httpClient.post(import.meta.env.VITE_API_URL + `/api/properties/toggle-status`, data), {
      success: `Property status has been updated!`,
      error: "Something went wrong",
      pending: `Updating property status...`
    })

    fetchPropertyFns()
  }

  const setStateSelectedProperty = (key, value) => {
    if (key === 'cadastral_nr') {
      setSelectedPropertyValidation(createEmptyPropertyValidation())
    }
    setSelectedProperty((currentProperty) => {
      const nextProperty = { ...currentProperty, [key]: value }
      if (key === 'cadastral_nr') {
        const parsedIdentifier = parsePropertyIdentifier(value)
        nextProperty.cadastral_nr = parsedIdentifier.cadastralNr
        if (parsedIdentifier.partitionNr) nextProperty.partition_nr = parsedIdentifier.partitionNr
        nextProperty.cadastral_validation_status = 'unvalidated'
        nextProperty.cadastral_validated_at = null
      } else if (key === 'partition_nr') {
        nextProperty.partition_nr = normalizePartition(value).slice(0, 5)
      }
      return nextProperty
    })
  }

  const setStateNewProperty = (key, value) => {
    if (key === 'cadastral_nr') {
      setNewPropertyValidation(createEmptyPropertyValidation())
    }
    setNewPropertyObj((currentProperty) => {
      const nextProperty = { ...currentProperty, [key]: value }
      if (key === 'cadastral_nr') {
        const parsedIdentifier = parsePropertyIdentifier(value)
        nextProperty.cadastral_nr = parsedIdentifier.cadastralNr
        if (parsedIdentifier.partitionNr) nextProperty.partition_nr = parsedIdentifier.partitionNr
        nextProperty.cadastral_validation_status = 'unvalidated'
        nextProperty.cadastral_validated_at = null
      } else if (key === 'partition_nr') {
        nextProperty.partition_nr = normalizePartition(value).slice(0, 5)
      }
      return nextProperty
    })
  }

  const validateCadastralNr = async (cadastralNr) => {
    const response = await httpClient.post(
      import.meta.env.VITE_API_URL + `/api/properties/validate-cadastral`,
      { cadastral_nr: cadastralNr }
    )

    if (response.status === 400) {
      return {
        ...createEmptyPropertyValidation(),
        ...response.data,
      }
    }

    return response.data
  }

  const lookupCapakeyByAddress = async (criteria) => {
    const response = await httpClient.post(
      import.meta.env.VITE_API_URL + `/api/properties/lookup-cadastral-by-address`,
      criteria
    )

    return response
  }

  const fetchMunicipalities = async () => {
    setMunicipalitiesRequested(true)
    setLoadingMunicipalities(true)
    try {
      const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/properties/cadastral-municipalities`)
      if (response.status === 200) {
        setMunicipalities(response.data?.municipalities || [])
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Municipality lookup unavailable')
    } finally {
      setLoadingMunicipalities(false)
    }
  }

  const updateLookupCriteria = (setter, key, value) => {
    setter((currentCriteria) => ({
      ...currentCriteria,
      [key]: value,
    }))
  }

  const applyValidationToNewProperty = (validationData) => {
    setNewPropertyObj((currentProperty) => ({
      ...currentProperty,
      cadastral_nr: validationData.normalized_capakey || currentProperty.cadastral_nr,
      partition_nr: validationData.partition_nr || currentProperty.partition_nr || '',
      cadastral_validation_status: validationData.status === 'validated' ? 'validated' : validationData.status === 'not_found' ? 'not_found' : 'unvalidated',
      cadastral_validated_at: validationData.status === 'validated' ? new Date().toISOString() : null,
    }))
    setNewPropertyValidation(validationData)
  }

  const applyValidationToSelectedProperty = (validationData) => {
    setSelectedProperty((currentProperty) => ({
      ...currentProperty,
      cadastral_nr: validationData.normalized_capakey || currentProperty.cadastral_nr,
      partition_nr: validationData.partition_nr || currentProperty.partition_nr || '',
      cadastral_validation_status: validationData.status === 'validated' ? 'validated' : validationData.status === 'not_found' ? 'not_found' : 'unvalidated',
      cadastral_validated_at: validationData.status === 'validated' ? new Date().toISOString() : null,
    }))
    setSelectedPropertyValidation(validationData)
  }

  const runNewPropertyValidation = async (cadastralNr) => {
    if (!cadastralNr) {
      toast.error('Cadastral ID is required')
      return
    }

    setValidatingNewProperty(true)
    try {
      const validationData = await validateCadastralNr(cadastralNr)
      applyValidationToNewProperty(validationData)

      if (validationData.status === 'validated') {
        toast.success('Official parcel match found')
      } else if (validationData.status === 'not_found') {
        toast.warning(validationData.message)
      } else {
        toast.error(validationData.message)
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Capakey validation unavailable'
      setNewPropertyValidation({
        ...createEmptyPropertyValidation(),
        status: 'service_error',
        message,
      })
      toast.error(message)
    } finally {
      setValidatingNewProperty(false)
    }
  }

  const handleValidateNewProperty = async () => {
    await runNewPropertyValidation(newPropertyObj.cadastral_nr)
  }

  const runSelectedPropertyValidation = async (cadastralNr) => {
    if (!cadastralNr) {
      toast.error('Cadastral ID is required')
      return
    }

    setValidatingSelectedProperty(true)
    try {
      const validationData = await validateCadastralNr(cadastralNr)
      applyValidationToSelectedProperty(validationData)

      if (validationData.status === 'validated') {
        toast.success('Official parcel match found')
      } else if (validationData.status === 'not_found') {
        toast.warning(validationData.message)
      } else {
        toast.error(validationData.message)
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Capakey validation unavailable'
      setSelectedPropertyValidation({
        ...createEmptyPropertyValidation(),
        status: 'service_error',
        message,
      })
      toast.error(message)
    } finally {
      setValidatingSelectedProperty(false)
    }
  }

  const handleValidateSelectedProperty = async () => {
    await runSelectedPropertyValidation(selectedProperty?.cadastral_nr)
  }

  const handleLookupNewPropertyCapakey = async () => {
    setSearchingNewPropertyCapakey(true)
    try {
      const response = await lookupCapakeyByAddress(newPropertyLookupCriteria)
      if (response.status === 400) {
        setNewPropertyLookupResults([])
        setNewPropertyLookupMessage(response.data?.message || 'Address lookup needs more details')
        toast.error(response.data?.message || 'Address lookup needs more details')
        return
      }

      const matches = response.data?.matches || []
      setNewPropertyLookupResults(matches)
      setNewPropertyLookupMessage(response.data?.message || '')

      if (matches.length > 0) {
        toast.success(`Found ${matches.length} official address match${matches.length === 1 ? '' : 'es'}`)
      } else {
        toast.warning(response.data?.message || 'No matching address found')
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Address lookup unavailable'
      setNewPropertyLookupResults([])
      setNewPropertyLookupMessage(message)
      toast.error(message)
    } finally {
      setSearchingNewPropertyCapakey(false)
    }
  }

  const handleLookupSelectedPropertyCapakey = async () => {
    setSearchingSelectedPropertyCapakey(true)
    try {
      const response = await lookupCapakeyByAddress(selectedPropertyLookupCriteria)
      if (response.status === 400) {
        setSelectedPropertyLookupResults([])
        setSelectedPropertyLookupMessage(response.data?.message || 'Address lookup needs more details')
        toast.error(response.data?.message || 'Address lookup needs more details')
        return
      }

      const matches = response.data?.matches || []
      setSelectedPropertyLookupResults(matches)
      setSelectedPropertyLookupMessage(response.data?.message || '')

      if (matches.length > 0) {
        toast.success(`Found ${matches.length} official address match${matches.length === 1 ? '' : 'es'}`)
      } else {
        toast.warning(response.data?.message || 'No matching address found')
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Address lookup unavailable'
      setSelectedPropertyLookupResults([])
      setSelectedPropertyLookupMessage(message)
      toast.error(message)
    } finally {
      setSearchingSelectedPropertyCapakey(false)
    }
  }

  const applyLookupCandidateToNewProperty = async (candidate, result) => {
    setNewPropertyLookupIsOpen(false)
    setNewPropertyValidation(createEmptyPropertyValidation())
    setNewPropertyObj((currentProperty) => ({
      ...currentProperty,
      cadastral_nr: candidate.capakey,
      partition_nr: '',
      location: result.address || currentProperty.location,
      cadastral_validation_status: 'unvalidated',
      cadastral_validated_at: null,
    }))
    await runNewPropertyValidation(candidate.capakey)
  }

  const applyLookupCandidateToSelectedProperty = async (candidate, result) => {
    setSelectedPropertyLookupIsOpen(false)
    setSelectedPropertyValidation(createEmptyPropertyValidation())
    setSelectedProperty((currentProperty) => ({
      ...currentProperty,
      cadastral_nr: candidate.capakey,
      partition_nr: '',
      location: result.address || currentProperty.location,
      cadastral_validation_status: 'unvalidated',
      cadastral_validated_at: null,
    }))
    await runSelectedPropertyValidation(candidate.capakey)
  }

  const applySuggestedLocationToNewPropertyFromAddress = (address) => {
    if (!address) return
    setStateNewProperty('location', address)
    toast.success('Official address copied to location')
  }

  const applySuggestedLocationToSelectedPropertyFromAddress = (address) => {
    if (!address) return
    setStateSelectedProperty('location', address)
    toast.success('Official address copied to location')
  }

  const handleTransactionClick = (transactionID) => { navigate(`/transactions/${transactionID}`) }

  const fetchProperties = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/properties/get`)
    if (response.status == 200) {
      setProperties(response.data)
    }
  }

  const fetchPropertiesOfUnit = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/properties/properties-of-unit?unitID=${unit.id}`)
    if (response.status == 200) {
      setPropertiesOfUnit(response.data)
    }
  }

  const fetchPropertyFns = () => {
    Promise.all([
      fetchProperties(),
      fetchPropertiesOfUnit()
    ])
  }

  const handleCreateProperty = async () => {
    if (!newPropertyObj.cadastral_nr) {
      toast.error("Cadastral ID is required")
      return false
    }
    if (!isValidPartition(newPropertyObj.partition_nr)) {
      toast.error('Partition must use format P0017')
      return false
    }

    const data = { ...newPropertyObj, unitID: unit.id }

    try {
      const createResponse = await httpClient.post(
        import.meta.env.VITE_API_URL + `/api/properties/create`,
        data
      )
      if (createResponse.status !== 200) {
        toast.error(createResponse.data?.error || 'Failed to create property')
        return false
      }

      toast.success('Property has been created!')
      // Reset form and fetch updated data
      setNewPropertyObj(initialPropertyState)
      setNewPropertyValidation(createEmptyPropertyValidation())
      setNewPropertyLookupIsOpen(false)
      setNewPropertyLookupCriteria(createEmptyCapakeyLookupCriteria())
      setNewPropertyLookupResults([])
      setNewPropertyLookupMessage('')
      setCreatingProperty(false)
      fetchPropertyFns()

      return true
    } catch (error) {
      console.error("Error creating property:", error)
      toast.error("Failed to create property")
      return false
    }
  }

  const renderPropertyListHeader = () => (
    <div className='property-list__header'>
      <div>Cadastral ID</div>
      <div>Location</div>
      <div>Type</div>
    </div>
  )

  const renderTransactionListHeader = () => (
    <div className='property-list__header transaction-list__header'>
      <div>ID</div>
      <div>Type</div>
      <div>Deed date</div>
    </div>
  )

  return (
    <div className='popout_body__content_container' id="unit-property-section">
      <div className="panel">
        <div className='property-section__panel-header'>
          <h2>Properties</h2>
        </div>
        <div className='action-buttons'>
          <Button
            variant='icon'
            iconOnly
            size='md'
            className='property-section__panel-action'
            onClick={() => { setLinkPropertyIsActive(!linkPropertyIsActive); setSelectedProperty(null) }}
            aria-label='Add or link property'
            title='Add or link property'
          >
            <FiPlus />
          </Button>
        </div>
        {loading.properties && (
          <div className='loading__container'>
            <CircularProgress color="inherit" />
          </div>
        )}
        {!loading.properties && (
          <div className='form__section'>
            <div className='form__section--content'>
              <div className='properties_list'>
                {propertiesOfUnit.length > 0 && renderPropertyListHeader()}
                {propertiesOfUnit
                  .sort((a, b) => (b.is_active === true) - (a.is_active === true))
                  .map((property, index) => (
                    <div
                      className={`property-item list ${property.is_active ? "" : "inactive"}`}
                      key={index}
                      onClick={() => { setLinkPropertyIsActive(false); setSelectedProperty(property) }}
                    >
                      <div>{formatPropertyIdentifier(property.cadastral_nr, property.partition_nr)}</div>
                      <div>{property.location}</div>
                      <div>{property.function}</div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className='panel'>
        {linkPropertyIsActive && !selectedProperty && (
          <div className='form__section'>
            <PopupWindow
              popupID={"popup-unit-property-create"}
              isActive={creatingProperty}
              setIsActive={setCreatingProperty}
            >
              {/* Property creation form */}
              <div className='date_note--popup'>
                <div className="input_field--combo-grid">
                  <div className='property-field-label-group'>
                    <label className="input_field__label" htmlFor="cadastral_nr">Cadastral ID</label>
                    <button
                      type='button'
                      className='property-field-helper-link'
                      onClick={() => setNewPropertyLookupIsOpen((currentValue) => !currentValue)}
                      disabled={validatingNewProperty || searchingNewPropertyCapakey}
                    >
                      {newPropertyLookupIsOpen ? 'hide address search' : 'search by address'}
                    </button>
                  </div>
                  <div className='cadastral-field-shell'>
                    <input
                      className="input_field__input_box--text cadastral-field-shell__input"
                      id="cadastral_nr"
                      type="text"
                      name="cadastral_nr"
                      value={newPropertyObj.cadastral_nr}
                      onChange={(e) => setStateNewProperty("cadastral_nr", e.currentTarget.value)}
                    />
                    <div className='cadastral-field-shell__actions'>
                      <Button
                        variant='secondary'
                        size='sm'
                        shape='pill'
                        leftIcon={<ValidationStatusIcon status={getEffectivePropertyValidationStatus(newPropertyObj.cadastral_validation_status, newPropertyValidation)} isLoading={validatingNewProperty} />}
                        onClick={handleValidateNewProperty}
                        disabled={validatingNewProperty}
                      >
                        {validatingNewProperty ? 'Validating...' : getPropertyValidationActionLabel(getEffectivePropertyValidationStatus(newPropertyObj.cadastral_validation_status, newPropertyValidation))}
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="input_field--combo-grid">
                  <label className="input_field__label" htmlFor="partition_nr">Partition</label>
                  <input
                    className={`input_field__input_box--text partition-field__input ${getPartitionHelper(newPropertyObj.partition_nr).isInvalid ? 'is-invalid' : ''}`}
                    id="partition_nr"
                    type="text"
                    name="partition_nr"
                    value={newPropertyObj.partition_nr}
                    onChange={(e) => setStateNewProperty('partition_nr', e.currentTarget.value)}
                    placeholder="P0017"
                    maxLength={5}
                    aria-describedby="partition_nr_help"
                    aria-invalid={getPartitionHelper(newPropertyObj.partition_nr).isInvalid}
                  />
                  <span id="partition_nr_help" className={`partition-field__help ${getPartitionHelper(newPropertyObj.partition_nr).isInvalid ? 'is-invalid' : ''}`}>
                    {getPartitionHelper(newPropertyObj.partition_nr).message}
                  </span>
                </div>
                {newPropertyLookupIsOpen && (
                  <CapakeyLookupPanel
                    idPrefix='create_property_lookup'
                    criteria={newPropertyLookupCriteria}
                    municipalities={municipalities}
                    municipalitiesLoading={loadingMunicipalities}
                    onCriteriaChange={(key, value) => updateLookupCriteria(setNewPropertyLookupCriteria, key, value)}
                    onSearch={handleLookupNewPropertyCapakey}
                    results={newPropertyLookupResults}
                    message={newPropertyLookupMessage}
                    isLoading={searchingNewPropertyCapakey}
                    onSelectCandidate={applyLookupCandidateToNewProperty}
                    disabled={validatingNewProperty}
                  />
                )}
                <PropertyValidationPanel
                  validation={newPropertyValidation}
                  onUseSuggestedLocation={applySuggestedLocationToNewPropertyFromAddress}
                  disabled={validatingNewProperty}
                />
                <div className="input_field--combo-grid">
                  <label className="input_field__label" htmlFor="location">Location</label>
                  <input
                    className="input_field__input_box--text"
                    id="location"
                    type="text"
                    name="location"
                    value={newPropertyObj.location}
                    onChange={(e) => setStateNewProperty("location", e.currentTarget.value)}
                  />
                </div>
                <div className="input_field--combo-grid">
                  <label className="input_field__label" htmlFor="function">Type</label>
                  <input
                    className="input_field__input_box--text"
                    id="function"
                    type="text"
                    name="function"
                    value={newPropertyObj.function}
                    onChange={(e) => setStateNewProperty("function", e.currentTarget.value)}
                  />
                </div>
                <div className="input_field--combo-grid">
                  <label className="input_field__label" htmlFor="surface">Surface (m²)</label>
                  <input
                    className="input_field__input_box--text"
                    id="surface"
                    type="number"
                    name="surface"
                    value={newPropertyObj.surface}
                    onChange={(e) => setStateNewProperty("surface", e.currentTarget.value)}
                  />
                </div>
                <div className="input_field--combo-grid">
                  <label className="input_field__label" htmlFor="GFAa_underground">GFAa underground (m²)</label>
                  <input
                    className="input_field__input_box--text"
                    id="GFAa_underground"
                    type="number"
                    name="GFAa_underground"
                    value={newPropertyObj.GFAa_underground}
                    onChange={(e) => setStateNewProperty("GFAa_underground", e.currentTarget.value)}
                  />
                </div>
                <div className="input_field--combo-grid">
                  <label className="input_field__label" htmlFor="GFAa">GFAa (m²)</label>
                  <input
                    className="input_field__input_box--text"
                    id="GFAa"
                    type="number"
                    name="GFAa"
                    value={newPropertyObj.GFAa}
                    onChange={(e) => setStateNewProperty("GFAa", e.currentTarget.value)}
                  />
                </div>
                <div className="input_field--combo-grid">
                  <label className="input_field__label" htmlFor="GFAb">GFAb (m²)</label>
                  <input
                    className="input_field__input_box--text"
                    id="GFAb"
                    type="number"
                    name="GFAb"
                    value={newPropertyObj.GFAb}
                    onChange={(e) => setStateNewProperty("GFAb", e.currentTarget.value)}
                  />
                </div>
                <div className="input_field--combo-grid">
                  <label className="input_field__label" htmlFor="GFAc">GFAc (m²)</label>
                  <input
                    className="input_field__input_box--text"
                    id="GFAc"
                    type="number"
                    name="GFAc"
                    value={newPropertyObj.GFAc}
                    onChange={(e) => setStateNewProperty("GFAc", e.currentTarget.value)}
                  />
                </div>
                <div className="input_field--combo-grid">
                  <label className="input_field__label" htmlFor="EPC_label">EPC label</label>
                  <input
                    className="input_field__input_box--text"
                    id="EPC_label"
                    type="text"
                    name="EPC_label"
                    value={newPropertyObj.EPC_label}
                    onChange={(e) => setStateNewProperty("EPC_label", e.currentTarget.value)}
                  />
                </div>
                <div className="input_field--combo-grid co-ownership-fraction">
                  <label className="input_field__label">Co-ownership shares</label>
                  <div className="fraction-inputs">
                    <input
                      className="input_field__input_box--text"
                      type="number"
                      name="co_ownership_share_numerator"
                      value={newPropertyObj.co_ownership_share_numerator}
                      onChange={(e) => setStateNewProperty("co_ownership_share_numerator", e.currentTarget.value)}
                    />
                    <span className="fraction-divider">/</span>
                    <input
                      className="input_field__input_box--text"
                      type="number"
                      name="co_ownership_share_denominator"
                      value={newPropertyObj.co_ownership_share_denominator}
                      onChange={(e) => setStateNewProperty("co_ownership_share_denominator", e.currentTarget.value)}
                    />
                  </div>
                </div>
                <div className="input_field">
                  <label className="input_field__label" htmlFor="description">Description</label>
                  <textarea
                    className="input_field__input_box--text-area"
                    id="description"
                    type="text"
                    name="description"
                    value={newPropertyObj.description}
                    onChange={(e) => setStateNewProperty("description", e.currentTarget.value)}
                  />
                </div>
              </div>
            </PopupWindow>

          <h2>Connect property</h2>
          <div className='action-buttons'>
            <Button variant='secondary' size='sm' leftIcon={<FiPlus />} onClick={() => setCreatingProperty(true)}>
              Create new
            </Button>
          </div>
          <input
            className='search-bar--list'
            type="text"
            name="property_name"
            value={propertySearchValue}
            onChange={(e) => setPropertySearchValue(e.currentTarget.value)}
            placeholder="Search property"
          />
          <div className='properties_list'>
            {properties
              .filter(property => !propertiesOfUnit.some(unitProperty => unitProperty?.id === property?.id))
              .filter(property => (
                property?.cadastral_nr?.toLowerCase().includes(propertySearchValue.toLowerCase()) ||
                  property?.partition_nr?.toLowerCase().includes(propertySearchValue.toLowerCase()) ||
                  formatPropertyIdentifier(property?.cadastral_nr, property?.partition_nr).toLowerCase().includes(propertySearchValue.toLowerCase()) ||
                  property?.location?.toLowerCase()?.includes(propertySearchValue.toLowerCase()) ||
                  property?.function?.toLowerCase()?.includes(propertySearchValue.toLowerCase())
                )).length > 0 && renderPropertyListHeader()}
              {properties
                .filter(property => !propertiesOfUnit.some(unitProperty => unitProperty?.id === property?.id))
                .filter(property => (
                  property?.cadastral_nr?.toLowerCase().includes(propertySearchValue.toLowerCase()) ||
                    property?.partition_nr?.toLowerCase().includes(propertySearchValue.toLowerCase()) ||
                    formatPropertyIdentifier(property?.cadastral_nr, property?.partition_nr).toLowerCase().includes(propertySearchValue.toLowerCase()) ||
                    property?.location?.toLowerCase()?.includes(propertySearchValue.toLowerCase()) ||
                  property?.function?.toLowerCase()?.includes(propertySearchValue.toLowerCase())
                ))
                .map((property, index) => (
                  <div className={`property-item ${property?.is_active ? "" : "inactive"}`} key={index} onClick={() => handleLinkProperty(property.id)}>
                    <div>{formatPropertyIdentifier(property?.cadastral_nr, property?.partition_nr)}</div>
                    <div>{property?.location}</div>
                    <div>{property?.function}</div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {selectedProperty && (
          <div className='form__section'>
            <div className='title_container'>
              <h2
                className={`click-title ${selectedPropertySection === "details" ? "selected" : ""}`}
                onClick={() => setSelectedPropertySection("details")}
              >
                Property details
              </h2>
              <h2
                className={`click-title ${selectedPropertySection === "transactions" ? "selected" : ""}`}
                onClick={() => setSelectedPropertySection("transactions")}
              >
                Transactions
              </h2>
            </div>

            {selectedPropertySection === "details" && (
              <>
                <div className='action-buttons'>
                  <Button variant='icon' iconOnly title="Edit property details" onClick={() => setEditProperty(!editProperty)} aria-label='Edit property details'>
                    <FaRegEdit />
                  </Button>
                  <Button variant='icon' iconOnly title="Toggle active state" onClick={handleTogglePropertyStatus} aria-label='Toggle active state'>
                    <HiMiniPower />
                  </Button>
                  <Button variant='icon' tone='red' iconOnly title="Unlink property" onClick={handleUnlinkProperty} aria-label='Unlink property'>
                    <GoUnlink />
                  </Button>
                </div>
                <div className='property_details--container'>
                  {renderPropertyDetailsActions()}
                  <div className='property_details--content'>
                    {/* Property details form fields */}
                    <div className='current-owners-section'>
                      <h3>Current Owners</h3>
                      <div className='owners-list'>
                        {selectedProperty.current_owners?.map((owner, index) => (
                          <div
                            key={index}
                            className='owner-item'
                            onClick={() => navigate(`/${owner.type}s/${owner.id}`)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className='owner-info'>
                              <span className='owner-name'>{owner.name}</span>
                              <span className='owner-type'>{owner.ownership_type}</span>
                            </div>
                            <div className='owner-percentage'>
                              {owner.ownership_percentage}%
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className={`input_field--simple wide-field-row ${editProperty ? "edit" : ""}`}>
                      <div className='property-field-label-group'>
                        <label className="input_field__label" htmlFor="cadastral_nr">Cadastral ID</label>
                        {editProperty && (
                          <button
                            type='button'
                            className='property-field-helper-link'
                            onClick={() => setSelectedPropertyLookupIsOpen(true)}
                            disabled={validatingSelectedProperty || searchingSelectedPropertyCapakey}
                          >
                            search by address
                          </button>
                        )}
                      </div>
                      <div className='cadastral-field-shell property-detail'>
                        <input
                          className="input_field__input_box--text cadastral-field-shell__input"
                          id="cadastral_nr"
                          type="text"
                          name="cadastral_nr"
                          value={selectedProperty.cadastral_nr}
                          onChange={(e) => setStateSelectedProperty("cadastral_nr", e.currentTarget.value)}
                          disabled={!editProperty}
                        />
                        <div className='cadastral-field-shell__actions'>
                          {editProperty && (
                            <Button
                              variant='secondary'
                              size='sm'
                              shape='pill'
                              leftIcon={<ValidationStatusIcon status={getEffectivePropertyValidationStatus(selectedProperty.cadastral_validation_status, selectedPropertyValidation)} isLoading={validatingSelectedProperty} />}
                              onClick={handleValidateSelectedProperty}
                              disabled={validatingSelectedProperty}
                            >
                              {validatingSelectedProperty ? 'Validating...' : getPropertyValidationActionLabel(getEffectivePropertyValidationStatus(selectedProperty.cadastral_validation_status, selectedPropertyValidation))}
                            </Button>
                          )}
                          {!editProperty && <ValidationStatusIcon status={selectedProperty.cadastral_validation_status} />}
                        </div>
                      </div>
                    </div>
                    <div className={`input_field--simple wide-field-row ${editProperty ? "edit" : ""}`}>
                      <div className='property-field-label-group'>
                        <label className="input_field__label" htmlFor="selected_partition_nr">Partition</label>
                        <span id="selected_partition_nr_help" className={`partition-field__help ${getPartitionHelper(selectedProperty.partition_nr).isInvalid ? 'is-invalid' : ''}`}>
                          {getPartitionHelper(selectedProperty.partition_nr).message}
                        </span>
                      </div>
                      <input
                        className={`input_field__input_box--text partition-field__input ${getPartitionHelper(selectedProperty.partition_nr).isInvalid ? 'is-invalid' : ''}`}
                        id="selected_partition_nr"
                        type="text"
                        name="partition_nr"
                        value={selectedProperty.partition_nr || ''}
                        onChange={(e) => setStateSelectedProperty('partition_nr', e.currentTarget.value)}
                        placeholder="P0017"
                        maxLength={5}
                        disabled={!editProperty}
                        aria-describedby="selected_partition_nr_help"
                        aria-invalid={getPartitionHelper(selectedProperty.partition_nr).isInvalid}
                      />
                    </div>
                    <PropertyValidationPanel
                      validation={selectedPropertyValidation}
                      onUseSuggestedLocation={applySuggestedLocationToSelectedPropertyFromAddress}
                      disabled={validatingSelectedProperty || !editProperty}
                    />
                    <div className={`input_field--simple wide-field-row ${editProperty ? "edit" : ""}`}>
                      <label className="input_field__label" htmlFor="location">Location</label>
                      <input
                        className="input_field__input_box--text"
                        id="location"
                        type="text"
                        name="location"
                        value={selectedProperty.location}
                        onChange={(e) => setStateSelectedProperty("location", e.currentTarget.value)}
                        disabled={!editProperty}
                      />
                    </div>
                    <div className={`input_field--simple ${editProperty ? "edit" : ""}`}>
                      <label className="input_field__label" htmlFor="function">Type</label>
                      <input
                        className="input_field__input_box--text"
                        id="function"
                        type="text"
                        name="function"
                        value={selectedProperty.function}
                        onChange={(e) => setStateSelectedProperty("function", e.currentTarget.value)}
                        disabled={!editProperty}
                      />
                    </div>
                    <div className={`input_field--simple ${editProperty ? "edit" : ""}`}>
                      <label className="input_field__label" htmlFor="surface">Surface (m²)</label>
                      <input
                        className="input_field__input_box--text"
                        id="surface"
                        type="number"
                        name="surface"
                        value={selectedProperty.surface}
                        onChange={(e) => setStateSelectedProperty("surface", e.currentTarget.value)}
                        disabled={!editProperty}
                      />
                    </div>
                    <div className={`input_field--simple ${editProperty ? "edit" : ""}`}>
                      <label className="input_field__label" htmlFor="GFAa_underground">GFAa underground (m²)</label>
                      <input
                        className="input_field__input_box--text"
                        id="GFAa_underground"
                        type="number"
                        name="GFAa_underground"
                        value={selectedProperty.GFAa_underground}
                        onChange={(e) => setStateSelectedProperty("GFAa_underground", e.currentTarget.value)}
                        disabled={!editProperty}
                      />
                    </div>
                    <div className={`input_field--simple ${editProperty ? "edit" : ""}`}>
                      <label className="input_field__label" htmlFor="GFAa">GFAa (m²)</label>
                      <input
                        className="input_field__input_box--text"
                        id="GFAa"
                        type="number"
                        name="GFAa"
                        value={selectedProperty.GFAa}
                        onChange={(e) => setStateSelectedProperty("GFAa", e.currentTarget.value)}
                        disabled={!editProperty}
                      />
                    </div>
                    <div className={`input_field--simple ${editProperty ? "edit" : ""}`}>
                      <label className="input_field__label" htmlFor="GFAb">GFAb (m²)</label>
                      <input
                        className="input_field__input_box--text"
                        id="GFAb"
                        type="number"
                        name="GFAb"
                        value={selectedProperty.GFAb}
                        onChange={(e) => setStateSelectedProperty("GFAb", e.currentTarget.value)}
                        disabled={!editProperty}
                      />
                    </div>
                    <div className={`input_field--simple ${editProperty ? "edit" : ""}`}>
                      <label className="input_field__label" htmlFor="GFAc">GFAc (m²)</label>
                      <input
                        className="input_field__input_box--text"
                        id="GFAc"
                        type="number"
                        name="GFAc"
                        value={selectedProperty.GFAc}
                        onChange={(e) => setStateSelectedProperty("GFAc", e.currentTarget.value)}
                        disabled={!editProperty}
                      />
                    </div>
                    <div className={`input_field--simple ${editProperty ? "edit" : ""}`}>
                      <label className="input_field__label" htmlFor="EPC_label">EPC label</label>
                      <input
                        className="input_field__input_box--text"
                        id="EPC_label"
                        type="text"
                        name="EPC_label"
                        value={selectedProperty.EPC_label}
                        onChange={(e) => setStateSelectedProperty("EPC_label", e.currentTarget.value)}
                        disabled={!editProperty}
                      />
                    </div>
                    <div className={`input_field--simple ${editProperty ? "edit" : ""}`}>
                      <label className="input_field__label">Co-ownership shares</label>
                      <div className="fraction-inputs">
                        <input
                          className="input_field__input_box--text"
                          type="number"
                          name="co_ownership_share_numerator"
                          value={selectedProperty.co_ownership_share_numerator}
                          onChange={(e) => setStateSelectedProperty("co_ownership_share_numerator", e.currentTarget.value)}
                          disabled={!editProperty}
                        />
                        <span className="fraction-divider">/</span>
                        <input
                          className="input_field__input_box--text"
                          type="number"
                          name="co_ownership_share_denominator"
                          value={selectedProperty.co_ownership_share_denominator}
                          onChange={(e) => setStateSelectedProperty("co_ownership_share_denominator", e.currentTarget.value)}
                          disabled={!editProperty}
                        />
                      </div>
                    </div>
                    <div className={`input_field--simple grid-2 ${editProperty ? "edit" : ""}`}>
                      <label className="input_field__label" htmlFor="description">Description</label>
                      <textarea
                        className="input_field__input_box--text-area"
                        id="description"
                        type="text"
                        name="description"
                        value={selectedProperty.description}
                        onChange={(e) => setStateSelectedProperty("description", e.currentTarget.value)}
                        disabled={!editProperty}
                      />
                    </div>
                  </div>
                  <PopupWindow
                    popupID={'popup-unit-property-lookup-selected'}
                    isActive={selectedPropertyLookupIsOpen}
                    setIsActive={setSelectedPropertyLookupIsOpen}
                  >
                    <div className='capakey-lookup-modal'>
                      <CapakeyLookupPanel
                        idPrefix='selected_property_lookup'
                        criteria={selectedPropertyLookupCriteria}
                        municipalities={municipalities}
                        municipalitiesLoading={loadingMunicipalities}
                        onCriteriaChange={(key, value) => updateLookupCriteria(setSelectedPropertyLookupCriteria, key, value)}
                        onSearch={handleLookupSelectedPropertyCapakey}
                        results={selectedPropertyLookupResults}
                        message={selectedPropertyLookupMessage}
                        isLoading={searchingSelectedPropertyCapakey}
                        onSelectCandidate={applyLookupCandidateToSelectedProperty}
                        disabled={validatingSelectedProperty || !editProperty}
                      />
                    </div>
                  </PopupWindow>
                </div>
              </>
            )}

            {selectedPropertySection === "transactions" && (
              <div className='transactions_list'>
                {selectedProperty.transactions?.length > 0 && renderTransactionListHeader()}
                {selectedProperty.transactions
                  .sort((a, b) => new Date(b.transaction_id.deed_date) - new Date(a.transaction_id.deed_date))
                  .map((transaction, index) => (
                    <div
                      className='transaction-item'
                      key={index}
                      onClick={() => handleTransactionClick(transaction.transaction_id.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div>id: {transaction.transaction_id.id}</div>
                      <div>{transaction.transaction_id.type}</div>
                      <div>{formatDate(transaction.transaction_id.deed_date)}</div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Transaction popup removed in favor of dedicated page */}
    </div>
  )
})

export default PropertySection 
